import {cloneDeep, isEmpty, isNil, isNumber, isPlainObject, isString} from 'lodash';
import {ApplySpecOpsResult, DATE_STYLES, FieldCatalog, FieldCatalogEntry, FormatSpec, NUMBER_STYLES, RejectedSpecOp} from './SpecTypes';

/** Thrown by a reducer for an invalid op - the op is skipped and reported, the rest still apply. */
export class OpError extends Error {}

export function check(condition: any, reason: string): asserts condition {
    if (!condition) throw new OpError(reason);
}

/**
 * Apply ops one at a time, each to a copy, so a failing op can't leave a half-applied change
 * behind. `applyOp` mutates the draft it is given and throws OpError to reject an op. It may return
 * a warning for an op it applied in part.
 */
export function applyEach<S, O>(spec: S, ops: O[], applyOp: (draft: S, op: O) => string | void): ApplySpecOpsResult<S, O> {
    let ret = cloneDeep(spec);
    const applied: O[] = [],
        rejected: RejectedSpecOp<O>[] = [],
        warnings: string[] = [];

    for (const op of ops ?? []) {
        const draft = cloneDeep(ret);
        try {
            check(isPlainObject(op) && isString((op as any).op), 'Malformed op');
            const warning = applyOp(draft, op);
            ret = draft;
            applied.push(op);
            if (isString(warning)) warnings.push(warning);
        } catch (e) {
            if (!(e instanceof OpError)) throw e;
            rejected.push({op, reason: e.message});
        }
    }

    return {spec: ret, applied, rejected, warnings};
}

export function checkField(field: string, catalog: FieldCatalog): FieldCatalogEntry {
    check(isString(field) && !isEmpty(field), 'field is required');
    const entry = catalog.find(f => f.name === field);
    check(entry, `Unknown field '${field}'`);
    return entry;
}

export function validateFormat(f: FormatSpec, label: string) {
    check(isPlainObject(f), `${label}: format must be an object`);
    check(['text', 'number', 'date', 'bool'].includes(f.type), `${label}: unknown format type '${f.type}'`);
    if (!isNil(f.numberStyle)) check(NUMBER_STYLES.includes(f.numberStyle), `${label}: unknown numberStyle '${f.numberStyle}'`);
    if (!isNil(f.dateStyle)) check(DATE_STYLES.includes(f.dateStyle), `${label}: unknown dateStyle '${f.dateStyle}'`);
    if (!isNil(f.precision))
        check(f.precision === 'auto' || (isNumber(f.precision) && f.precision >= 0 && f.precision <= 12), `${label}: precision must be 0-12 or 'auto'`);
}

/** Merge a format change into an existing format, so e.g. {precision: 2} keeps the number style. A new type starts a new format. */
export function mergeFormat(prev: FormatSpec, next: Partial<FormatSpec>): FormatSpec {
    return prev && (!next.type || next.type === prev.type) ? {...prev, ...next} : {type: 'text', ...next};
}
